import React, { useState, useMemo } from 'react';
import InteractiveChart from './InteractiveChart.jsx';

function ComparisonTab({ expirations }) {
  const [period1, setPeriod1] = useState({
    start: expirations[Math.min(100, expirations.length - 1)].date,
    end: expirations[Math.min(50, expirations.length - 1)].date,
  });

  const [period2, setPeriod2] = useState({
    start: expirations[Math.min(50, expirations.length - 1)].date,
    end: expirations[0].date,
  });

  // Calculate statistics for each period
  const compareStats = useMemo(() => {
    const getPeriodData = (start, end) => {
      return expirations.filter(e => e.date >= start && e.date <= end);
    };

    const calcStats = (data) => {
      if (data.length < 2) return null;

      const changes = [];
      for (let i = 0; i < data.length - 1; i++) {
        const change = ((data[i].price - data[i + 1].price) / data[i + 1].price) * 100;
        changes.push(change);
      }

      const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
      const positiveCount = changes.filter(c => c > 0).length;
      const negativeCount = changes.filter(c => c < 0).length;
      const maxChange = Math.max(...changes);
      const minChange = Math.min(...changes);
      
      const variance = changes.reduce((sum, c) => sum + Math.pow(c - avgChange, 2), 0) / changes.length;
      const stdDev = Math.sqrt(variance);

      return {
        count: data.length,
        avgChange: avgChange.toFixed(2),
        stdDev: stdDev.toFixed(2),
        positiveCount,
        negativeCount,
        winRate: ((positiveCount / changes.length) * 100).toFixed(1),
        maxChange: maxChange.toFixed(2),
        minChange: minChange.toFixed(2),
        totalReturn: (((data[0].price - data[data.length - 1].price) / data[data.length - 1].price) * 100).toFixed(2),
      };
    };

    const data1 = getPeriodData(period1.start, period1.end);
    const data2 = getPeriodData(period2.start, period2.end);

    return {
      period1: calcStats(data1),
      period2: calcStats(data2),
      data1,
      data2,
    };
  }, [expirations, period1, period2]);

  const chartData1 = useMemo(() => {
    if (!compareStats.data1) return [];
    return compareStats.data1.map(e => ({
      date: e.date,
      value: e.price,
      label: e.date,
    }));
  }, [compareStats.data1]);

  const chartData2 = useMemo(() => {
    if (!compareStats.data2) return [];
    return compareStats.data2.map(e => ({
      date: e.date,
      value: e.price,
      label: e.date,
    }));
  }, [compareStats.data2]);

  return (
    <div className="comparison-tab">
      <div className="comparison-header">
        <h2>⚖️ השוואת תקופות</h2>
        <p className="tab-description">
          השווה ביצועים בין שתי תקופות שונות לזיהוי שינויים במגמות
        </p>
      </div>

      {/* Period Selection */}
      <div className="period-selection-grid">
        <div className="period-selector">
          <h3>📅 תקופה 1</h3>
          <div className="date-inputs">
            <div>
              <label>תאריך התחלה</label>
              <input
                type="date"
                value={period1.start}
                onChange={(e) => setPeriod1({ ...period1, start: e.target.value })}
              />
            </div>
            <div>
              <label>תאריך סיום</label>
              <input
                type="date"
                value={period1.end}
                onChange={(e) => setPeriod1({ ...period1, end: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="period-selector">
          <h3>📅 תקופה 2</h3>
          <div className="date-inputs">
            <div>
              <label>תאריך התחלה</label>
              <input
                type="date"
                value={period2.start}
                onChange={(e) => setPeriod2({ ...period2, start: e.target.value })}
              />
            </div>
            <div>
              <label>תאריך סיום</label>
              <input
                type="date"
                value={period2.end}
                onChange={(e) => setPeriod2({ ...period2, end: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Results */}
      {compareStats.period1 && compareStats.period2 && (
        <>
          {/* Side by Side Stats */}
          <div className="comparison-stats-grid">
            <div className="comparison-column period1">
              <h3>תקופה 1</h3>
              <div className="stats-list">
                <div className="stat-row">
                  <span>תשואה כוללת:</span>
                  <strong className={parseFloat(compareStats.period1.totalReturn) >= 0 ? 'positive' : 'negative'}>
                    {compareStats.period1.totalReturn}%
                  </strong>
                </div>
                <div className="stat-row">
                  <span>ממוצע שינוי:</span>
                  <strong>{compareStats.period1.avgChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>סטיית תקן:</span>
                  <strong>{compareStats.period1.stdDev}%</strong>
                </div>
                <div className="stat-row">
                  <span>אחוז רווח:</span>
                  <strong>{compareStats.period1.winRate}%</strong>
                </div>
                <div className="stat-row">
                  <span>שינוי מקסימלי:</span>
                  <strong className="positive">+{compareStats.period1.maxChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>שינוי מינימלי:</span>
                  <strong className="negative">{compareStats.period1.minChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>סה"כ פקיעות:</span>
                  <strong>{compareStats.period1.count}</strong>
                </div>
              </div>
            </div>

            <div className="comparison-vs">
              <div className="vs-circle">VS</div>
            </div>

            <div className="comparison-column period2">
              <h3>תקופה 2</h3>
              <div className="stats-list">
                <div className="stat-row">
                  <span>תשואה כוללת:</span>
                  <strong className={parseFloat(compareStats.period2.totalReturn) >= 0 ? 'positive' : 'negative'}>
                    {compareStats.period2.totalReturn}%
                  </strong>
                </div>
                <div className="stat-row">
                  <span>ממוצע שינוי:</span>
                  <strong>{compareStats.period2.avgChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>סטיית תקן:</span>
                  <strong>{compareStats.period2.stdDev}%</strong>
                </div>
                <div className="stat-row">
                  <span>אחוז רווח:</span>
                  <strong>{compareStats.period2.winRate}%</strong>
                </div>
                <div className="stat-row">
                  <span>שינוי מקסימלי:</span>
                  <strong className="positive">+{compareStats.period2.maxChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>שינוי מינימלי:</span>
                  <strong className="negative">{compareStats.period2.minChange}%</strong>
                </div>
                <div className="stat-row">
                  <span>סה"כ פקיעות:</span>
                  <strong>{compareStats.period2.count}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Key Differences */}
          <div className="key-differences-section">
            <h3>🔍 הבדלים מרכזיים</h3>
            <div className="differences-grid">
              <div className="difference-card">
                <div className="diff-label">שינוי בתשואה</div>
                <div className={`diff-value ${
                  parseFloat(compareStats.period2.totalReturn) > parseFloat(compareStats.period1.totalReturn) ? 'positive' : 'negative'
                }`}>
                  {(parseFloat(compareStats.period2.totalReturn) - parseFloat(compareStats.period1.totalReturn)).toFixed(2)}%
                </div>
              </div>

              <div className="difference-card">
                <div className="diff-label">שינוי בתנודתיות</div>
                <div className={`diff-value ${
                  parseFloat(compareStats.period2.stdDev) > parseFloat(compareStats.period1.stdDev) ? 'negative' : 'positive'
                }`}>
                  {((parseFloat(compareStats.period2.stdDev) - parseFloat(compareStats.period1.stdDev)) / parseFloat(compareStats.period1.stdDev) * 100).toFixed(0)}%
                </div>
              </div>

              <div className="difference-card">
                <div className="diff-label">שינוי באחוז רווח</div>
                <div className={`diff-value ${
                  parseFloat(compareStats.period2.winRate) > parseFloat(compareStats.period1.winRate) ? 'positive' : 'negative'
                }`}>
                  {(parseFloat(compareStats.period2.winRate) - parseFloat(compareStats.period1.winRate)).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="comparison-charts">
            <div className="chart-wrapper">
              <h4>תקופה 1 - תנועת מחירים</h4>
              <InteractiveChart
                data={chartData1}
                type="area"
              />
            </div>

            <div className="chart-wrapper">
              <h4>תקופה 2 - תנועת מחירים</h4>
              <InteractiveChart
                data={chartData2}
                type="area"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ComparisonTab;
